import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import styled from 'styled-components';
import { DownArrow, UpArrow, DoubleArrow } from 'ui/icons';
import { useSortingContext } from 'utils/contexts/sorting';

import { SORTABLE_TABLE_COLUMNS_TO_PROPERTIES, TABLE_COLUMNS } from '../constants';
import { Cell } from './Cell';

const SortIconPlaceholder = styled(DoubleArrow).attrs({ 'data-test': 'sort-icon-placeholder' })`
  color: ${({ theme }) => theme.colors.textColors.textSecondary};
`;

type HeaderCellProps = {
  columnId: TABLE_COLUMNS;
  label: string;
};

const HeaderWrapper = styled(Cell)<{
  columnId: string;
  isBeingSorted: boolean;
}>`
  && {
    background: none;
    border: none;
    outline: none;
    white-space: nowrap;
    border-width: 0 0 ${({ theme }) => theme.spacing.values.single} 0;
    border-style: solid;
    border-color: #d6d6d6;
    span {
      display: inline-block;
      margin-right: ${({ theme }) => theme.spacing.values.quarter};
    }
    text-align: left;
    color: ${({ theme, isBeingSorted }) =>
      isBeingSorted ? theme.colors.primaryColors.purple100 : theme.colors.textColors.textPrimary};
    align-items: center;
    justify-content: flex-start;
    flex-direction: row;
  }
`;

const NON_ALTERNATING_COLOR_INDEX = 1;

export const HeaderCell = ({ columnId, label }: HeaderCellProps) => {
  const { sortBy, sortDirection, setSortBy, setSortDirection } = useSortingContext();
  const isSortable = columnId in SORTABLE_TABLE_COLUMNS_TO_PROPERTIES;
  const isBeingSorted = sortBy === columnId;
  const SortDirectionIcon = sortDirection === SORT_DIRECTIONS.ASCENDING ? UpArrow : DownArrow;
  const SortIcon = isBeingSorted ? SortDirectionIcon : SortIconPlaceholder;
  const onClick = () => {
    if (isSortable) {
      const toggledSortDirection =
        sortDirection === SORT_DIRECTIONS.ASCENDING
          ? SORT_DIRECTIONS.DESCENDING
          : SORT_DIRECTIONS.ASCENDING;
      setSortBy(columnId);
      setSortDirection(isBeingSorted ? toggledSortDirection : SORT_DIRECTIONS.ASCENDING);
    }
  };

  return (
    <HeaderWrapper
      isBeingSorted={isBeingSorted}
      as={isSortable ? 'button' : 'div'}
      data-test={`column-header-${columnId}`}
      columnId={columnId}
      key={columnId}
      rowIndex={NON_ALTERNATING_COLOR_INDEX}
      onClick={onClick}
    >
      <span>{label}</span>
      {isSortable && <SortIcon isColorInherited />}
    </HeaderWrapper>
  );
};
